import React from 'react';
import PropTypes from 'prop-types';
import { Box } from 'rebass';
import Modal from 'app/modal/v3';
import { layout } from '../tokens';
import { DetailImage, DetailTagline, DetailBody } from './styled';
import BundleComparison from './bundle-comparison';

// Plan-vs-plan comparison, shown instead of the usual marketing spread when the
// member taps a destination plan in Upgrade Bundle mode.
//
// Built in-app rather than embedding babyyearbooks.com/pages/bundles_comparison:
// that domain sends `Content-Security-Policy: frame-ancestors 'none'` —
// confirmed in practice, site-wide, not just on checkout. That header is
// enforced by the browser from the page's own response, so there was no
// client-side way to frame it (and no "might work in a different environment"
// case the way there was for the Shopify invoice, since this is the same
// domain). `BundleComparison` is a static recreation of that page's content.

// Reuses the existing marketing spreads for these features (the same assets used
// in their own onboarding/FTUE flows) rather than commissioning new art for this
// popover — the design screenshot's Milestones / Watch Me Grow / Growth Chart /
// Talking Family Tree detail views are exactly these book spreads.
import milestonesImage from 'collections/milestones/assets/milestones.png';
import watchMeGrowImage from 'collections/milestones/assets/watchMeGrow.png';
import talkingFamilyTreeImage from 'collections/milestones/assets/talkingFamilyTree.png';
import growthChartImage from 'collections/milestones/assets/growth-chart-preview.png';

const IMAGE_MATCHERS = [
  { test: /milestone/i, src: milestonesImage },
  { test: /watch me grow/i, src: watchMeGrowImage },
  { test: /talking family tree/i, src: talkingFamilyTreeImage },
  { test: /growth chart/i, src: growthChartImage },
];

// Purely decorative — picking which illustration to show never affects price,
// eligibility or what gets submitted at checkout, so this is not the "branch on
// sku" the API guide warns against.
const imageFor = title => IMAGE_MATCHERS.find(matcher => matcher.test.test(title || ''))?.src || null;

// "Milestones" product-detail popover from the design: title, book-spread image,
// a bold tagline, then the longer description. Centered on mobile; a 590px modal
// with a 480px-wide image on desktop (per the design's annotation).
//
// In Upgrade Bundle mode there's no per-plan marketing spread to show — instead
// this renders `BundleComparison` (see the CSP note above for why that isn't
// just the marketing page in an iframe).
const ProductDetailModal = ({ mode, option, onClose }) => {
  const isUpgrade = mode === 'upgrade';
  const image = !isUpgrade ? imageFor(option.title) : null;
  const title = !isUpgrade ? option.title : null;
  // Both are always server-provided text (guide §3.1) — never generated from the
  // SKU. `shortDescription` reads as the bold lead-in when present; `description`
  // is always shown below it.
  const tagline = !isUpgrade && option.shortDescription && option.shortDescription !== option.description
    ? option.shortDescription
    : null;

  return (
    <Modal
      title={title}
      close={{ onClose }}
      innerProps={{ maxWidth: layout.purchaseDetailModalWidth }}
      maxWidth={isUpgrade ? 1000 : 600}
      shadeProps={{ pt: [0, 4] }}
    >
      {isUpgrade ? (
        <BundleComparison />
      ) : (
        <Box px={[3, 4]} py={3} css="display: grid; grid-gap: 4px;">
          {image && <DetailImage src={image} alt="" />}
          {/* <DetailBody>{option.description || option.title}</DetailBody> */}
          {tagline && <DetailTagline dangerouslySetInnerHTML={{ __html: tagline }} />}
        </Box>
      )}
    </Modal>
  );
};

ProductDetailModal.propTypes = {
  mode: PropTypes.oneOf(['add-ons', 'upgrade']).isRequired,
  option: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    shortDescription: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProductDetailModal;
